import React from "react";
import styles from "./service-request-receipt-card.module.scss";
import { ServiceRequestInstance } from "../../../../../model/service-request/service-request-instance";
import { ServiceRequestReceiptIcon, CopyIcon, CheckIcon } from "../../../../../assets/icons";
import { formatCurrency, formatDateWithOrdinal, handleCopy } from "../../../../../utils";
import { formatDate, formatTime } from "../../../history/history.utils";

interface ServiceRequestReceiptCardProps {
	instance?: ServiceRequestInstance;
}

const formatStepDate = (value?: string): string => {
	if (!value) return "";
	return `${formatDate(value)}, ${formatTime(value).toLowerCase()}`;
};

const ServiceRequestReceiptCard: React.FC<ServiceRequestReceiptCardProps> = ({ instance }) => {
	const price = instance?.price;
	const hasAmount = !!price && price.amount !== 0;
	const amountText = hasAmount ? formatCurrency(price.amount, { currency: price.currency }) : "Free Service Request";
	const headerDateText = instance ? formatDateWithOrdinal(instance.updatedAt, instance.createdAt) : "";

	const historySteps = instance?.historyStepsData ?? [];

	const detailRows: { label: string; value: string }[] = [
		{ label: "Service Type", value: instance?.serviceRequest?.name ?? "" },
		...(hasAmount ? [{ label: "Amount", value: amountText }] : []),
		{ label: "Date", value: formatStepDate(instance?.createdAt) },
	];

	const transactionId = instance?.transaction?.id;
	const requestId = instance?.instanceId ?? (instance?.id != null ? String(instance.id) : "");

	return (
		<div className={styles.receipt}>
			{/* Header: amount + date + service icon */}
			<div className={styles.headerSection}>
				<div className={styles.headerInfo}>
					<span className={`${styles.amount} ${hasAmount ? styles.amountLarge : styles.amountSmall}`}>
						{amountText}
					</span>
					<span className={styles.headerDate}>{headerDateText}</span>
				</div>
				<div className={styles.iconCircle}>
					<ServiceRequestReceiptIcon className={styles.serviceIcon} />
				</div>
			</div>

			{/* Timeline: history steps */}
			{historySteps.length > 0 && (
				<>
					<div className={styles.dashedDivider} />
					<div className={styles.timelineSection}>
						{historySteps.map((step, i) => {
							const isLast = i === historySteps.length - 1;
							return (
								<React.Fragment key={step.id}>
									<div className={styles.timelineRow}>
										<div className={styles.timelineIcon}>
											<CheckIcon className={styles.timelineCheck} />
										</div>
										<div className={styles.timelineContent}>
											<span className={styles.timelineName}>{step.stepName}</span>
											<span className={styles.timelineDate}>{formatStepDate(step.createdAt)}</span>
										</div>
									</div>
									{!isLast && (
										<div className={styles.timelineConnector}>
											<span className={styles.timelineLine} />
										</div>
									)}
								</React.Fragment>
							);
						})}
					</div>
				</>
			)}

			{/* Details: service type / amount / date */}
			<div className={styles.dashedDivider} />
			<div className={styles.detailsSection}>
				{detailRows.map((row, i) => (
					<div key={i} className={styles.detailRow}>
						<span className={styles.detailLabel}>{row.label}</span>
						<span className={styles.detailValue}>{row.value}</span>
					</div>
				))}
			</div>

			{/* Transaction & request IDs */}
			<div className={styles.dashedDivider} />
			<div className={styles.idsSection}>
				{transactionId && (
					<div className={styles.idRow}>
						<span className={styles.detailLabel}>Transaction ID</span>
						<div className={styles.idValueWrap}>
							<span className={styles.idValue}>{transactionId}</span>
							<CopyIcon className={styles.copyIcon} onClick={() => handleCopy(transactionId)} />
						</div>
					</div>
				)}
				<div className={styles.idRow}>
					<span className={styles.detailLabel}>Request ID</span>
					<div className={styles.idValueWrap}>
						<span className={styles.idValue}>{requestId}</span>
						<CopyIcon className={styles.copyIcon} onClick={() => handleCopy(requestId)} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default ServiceRequestReceiptCard;
